import {
  AuthorizationType,
  ServerAuthorizationConfig,
} from "components/common/ServerAuthorizationConfig";
import { generateSecret } from "components/common/TokenEndpointConfig";
import { Button, HStack, VStack } from "components/ui";
import { toaster } from "components/ui/toaster";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdCheckCircle } from "react-icons/md";
import { ServerConfig, serverService } from "services/serverService";

interface AuthorizationConfigProps {
  serverId: string;
  onServerUpdated?: (updatedServer: ServerConfig) => void;
}

export const AuthorizationConfigComponent: React.FC<
  AuthorizationConfigProps
> = ({ serverId, onServerUpdated }) => {
  const { t } = useTranslation();
  const [server, setServer] = useState<ServerConfig | null>(null);
  const [loading, setLoading] = useState(true);

  // Authorization configuration state
  const [authorizationType, setAuthorizationType] =
    useState<AuthorizationType>("None");
  const [staticAuthHeaders, setStaticAuthHeaders] = useState("");
  const [dynamicAuthHeaders, setDynamicAuthHeaders] = useState("");
  const [allowAuthUpdate, setAllowAuthUpdate] = useState(true);
  const [authInstructionsUrl, setAuthInstructionsUrl] = useState("");
  // CustomAuthEndpoint state
  const [autoTokenEndpoint, setAutoTokenEndpoint] = useState("");
  const [autoTokenMethod, setAutoTokenMethod] = useState("POST");
  const [autoTokenRequestHeaders, setAutoTokenRequestHeaders] = useState("");
  const [autoTokenRequestBody, setAutoTokenRequestBody] = useState("");
  const [autoTokenRequestPlaceholders, setAutoTokenRequestPlaceholders] =
    useState("");
  const [autoTokenResponseExample, setAutoTokenResponseExample] = useState("");
  const [autoTokenResponsePath, setAutoTokenResponsePath] = useState("");
  const [autoTokenHeaderName, setAutoTokenHeaderName] =
    useState("Authorization");
  const [autoTokenHeaderFormat, setAutoTokenHeaderFormat] =
    useState("Bearer {token}");
  const [autoTokenSecret, setAutoTokenSecret] = useState("");
  // Refresh token state
  const [refreshTokenPath, setRefreshTokenPath] = useState("");
  const [refreshTokenEndpoint, setRefreshTokenEndpoint] = useState("");
  const [refreshTokenMethod, setRefreshTokenMethod] = useState("POST");
  const [refreshTokenRequestHeaders, setRefreshTokenRequestHeaders] =
    useState("");
  const [refreshTokenRequestBody, setRefreshTokenRequestBody] = useState("");
  const [refreshTokenResponsePath, setRefreshTokenResponsePath] = useState("");
  const [savingAuth, setSavingAuth] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    autoTokenEndpoint?: string;
    autoTokenResponsePath?: string;
    autoTokenHeaderName?: string;
    autoTokenHeaderFormat?: string;
  }>({});

  useEffect(() => {
    const loadServer = async () => {
      try {
        setLoading(true);
        const serverData = await serverService.getServerById(serverId);
        setServer(serverData);

        // Load authorization configuration
        setAuthorizationType(
          (serverData.authorizationType as AuthorizationType) || "None"
        );
        setStaticAuthHeaders(serverData.staticAuthHeaders || "");
        setDynamicAuthHeaders(serverData.dynamicAuthHeaders || "");
        setAllowAuthUpdate(serverData.allowAuthUpdate ?? true);
        setAuthInstructionsUrl(serverData.authInstructionsUrl || "");
        // Load CustomAuthEndpoint configuration
        setAutoTokenEndpoint(serverData.autoTokenEndpoint || "");
        setAutoTokenMethod(serverData.autoTokenMethod || "POST");
        setAutoTokenRequestHeaders(serverData.autoTokenRequestHeaders || "");
        setAutoTokenRequestBody(serverData.autoTokenRequestBody || "");
        setAutoTokenRequestPlaceholders(
          serverData.autoTokenRequestPlaceholders || ""
        );
        setAutoTokenResponseExample(serverData.autoTokenResponseExample || "");
        setAutoTokenResponsePath(serverData.autoTokenResponsePath || "");
        setAutoTokenHeaderName(
          serverData.autoTokenHeaderName || "Authorization"
        );
        setAutoTokenHeaderFormat(
          serverData.autoTokenHeaderFormat || "Bearer {token}"
        );
        setAutoTokenSecret(serverData.autoTokenSecret || "");
        setRefreshTokenPath(serverData.refreshTokenPath || "");
        setRefreshTokenEndpoint(serverData.refreshTokenEndpoint || "");
        setRefreshTokenMethod(serverData.refreshTokenMethod || "POST");
        setRefreshTokenRequestHeaders(
          serverData.refreshTokenRequestHeaders || ""
        );
        setRefreshTokenRequestBody(serverData.refreshTokenRequestBody || "");
        setRefreshTokenResponsePath(serverData.refreshTokenResponsePath || "");
      } catch (err: any) {
        toaster.create({
          title: t("app.servers.errors.failedToLoadDetails"),
          description: err.response?.data?.message || err.message,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadServer();
  }, [serverId, t]);

  const handleAuthConfigChange = (field: string, value: any) => {
    // Clear validation error for this field
    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    }

    switch (field) {
      case "authorizationType":
        setAuthorizationType(value);
        // Auto-generate secret when switching to CustomAuthEndpoint
        if (value === "CustomAuthEndpoint" && !autoTokenSecret) {
          setAutoTokenSecret(generateSecret());
        }
        break;
      case "staticAuthHeaders":
        setStaticAuthHeaders(value);
        break;
      case "dynamicAuthHeaders":
        setDynamicAuthHeaders(value);
        break;
      case "allowAuthUpdate":
        setAllowAuthUpdate(value);
        break;
      case "authInstructionsUrl":
        setAuthInstructionsUrl(value);
        break;
      case "autoTokenEndpoint":
        setAutoTokenEndpoint(value);
        break;
      case "autoTokenMethod":
        setAutoTokenMethod(value);
        break;
      case "autoTokenRequestHeaders":
        setAutoTokenRequestHeaders(value);
        break;
      case "autoTokenRequestBody":
        setAutoTokenRequestBody(value);
        break;
      case "autoTokenRequestPlaceholders":
        setAutoTokenRequestPlaceholders(value);
        break;
      case "autoTokenResponseExample":
        setAutoTokenResponseExample(value);
        break;
      case "autoTokenResponsePath":
        setAutoTokenResponsePath(value);
        break;
      case "autoTokenHeaderName":
        setAutoTokenHeaderName(value);
        break;
      case "autoTokenHeaderFormat":
        setAutoTokenHeaderFormat(value);
        break;
      case "autoTokenSecret":
        setAutoTokenSecret(value);
        break;
      case "refreshTokenPath":
        setRefreshTokenPath(value);
        break;
      case "refreshTokenEndpoint":
        setRefreshTokenEndpoint(value);
        break;
      case "refreshTokenMethod":
        setRefreshTokenMethod(value);
        break;
      case "refreshTokenRequestHeaders":
        setRefreshTokenRequestHeaders(value);
        break;
      case "refreshTokenRequestBody":
        setRefreshTokenRequestBody(value);
        break;
      case "refreshTokenResponsePath":
        setRefreshTokenResponsePath(value);
        break;
    }
  };

  const handleSaveAuthorization = async () => {
    // Validate CustomAuthEndpoint configuration
    if (authorizationType === "CustomAuthEndpoint") {
      const errors: typeof validationErrors = {};

      if (!autoTokenEndpoint || autoTokenEndpoint.trim() === "") {
        errors.autoTokenEndpoint = t(
          "app.servers.authorization.tokenEndpoint.urlRequired"
        );
      }
      if (!autoTokenResponsePath || autoTokenResponsePath.trim() === "") {
        errors.autoTokenResponsePath = t(
          "app.servers.authorization.tokenEndpoint.responsePathRequired"
        );
      }
      if (!autoTokenHeaderName || autoTokenHeaderName.trim() === "") {
        errors.autoTokenHeaderName = t(
          "app.servers.authorization.tokenEndpoint.headerNameRequired"
        );
      }
      if (!autoTokenHeaderFormat || autoTokenHeaderFormat.trim() === "") {
        errors.autoTokenHeaderFormat = t(
          "app.servers.authorization.tokenEndpoint.headerFormatRequired"
        );
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }
    }

    setValidationErrors({});

    try {
      setSavingAuth(true);
      const supportsStaticHeaders = authorizationType !== "None";
      const supportsDynamicHeaders =
        authorizationType === "CustomToken" || authorizationType === "OAuth";
      const isCustomAuthEndpoint = authorizationType === "CustomAuthEndpoint";

      const updatedServer = await serverService.updateServer(serverId, {
        authorizationType,
        staticAuthHeaders: supportsStaticHeaders ? staticAuthHeaders : "",
        dynamicAuthHeaders: supportsDynamicHeaders ? dynamicAuthHeaders : "",
        allowAuthUpdate,
        authInstructionsUrl: isCustomAuthEndpoint ? "" : authInstructionsUrl,
        // CustomAuthEndpoint fields
        autoTokenEndpoint: isCustomAuthEndpoint ? autoTokenEndpoint : "",
        autoTokenMethod: isCustomAuthEndpoint ? autoTokenMethod : "",
        autoTokenRequestHeaders: isCustomAuthEndpoint
          ? autoTokenRequestHeaders
          : "",
        autoTokenRequestBody: isCustomAuthEndpoint ? autoTokenRequestBody : "",
        autoTokenRequestPlaceholders: isCustomAuthEndpoint
          ? autoTokenRequestPlaceholders
          : "",
        autoTokenResponseExample: isCustomAuthEndpoint
          ? autoTokenResponseExample
          : "",
        autoTokenResponsePath: isCustomAuthEndpoint
          ? autoTokenResponsePath
          : "",
        autoTokenHeaderName: isCustomAuthEndpoint ? autoTokenHeaderName : "",
        autoTokenHeaderFormat: isCustomAuthEndpoint
          ? autoTokenHeaderFormat
          : "",
        autoTokenSecret: isCustomAuthEndpoint ? autoTokenSecret : "",
        refreshTokenPath: isCustomAuthEndpoint ? refreshTokenPath : "",
        refreshTokenEndpoint: isCustomAuthEndpoint ? refreshTokenEndpoint : "",
        refreshTokenMethod: isCustomAuthEndpoint ? refreshTokenMethod : "",
        refreshTokenRequestHeaders: isCustomAuthEndpoint
          ? refreshTokenRequestHeaders
          : "",
        refreshTokenRequestBody: isCustomAuthEndpoint
          ? refreshTokenRequestBody
          : "",
        refreshTokenResponsePath: isCustomAuthEndpoint
          ? refreshTokenResponsePath
          : "",
      });

      toaster.create({
        title: t("app.servers.authorization.saved"),
        description: t("app.servers.authorization.savedDesc"),
        type: "success",
      });

      if (onServerUpdated) {
        onServerUpdated(updatedServer);
      }
    } catch (err: any) {
      toaster.create({
        title: t("app.servers.authorization.saveFailed"),
        description: err.response?.data?.message || t("app.common.error"),
        type: "error",
      });
    } finally {
      setSavingAuth(false);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <VStack gap={4} align="stretch">
      <ServerAuthorizationConfig
        authorizationType={authorizationType}
        staticAuthHeaders={staticAuthHeaders}
        dynamicAuthHeaders={dynamicAuthHeaders}
        allowAuthUpdate={allowAuthUpdate}
        authInstructionsUrl={authInstructionsUrl}
        autoTokenEndpoint={autoTokenEndpoint}
        autoTokenMethod={autoTokenMethod}
        autoTokenRequestHeaders={autoTokenRequestHeaders}
        autoTokenRequestBody={autoTokenRequestBody}
        autoTokenRequestPlaceholders={autoTokenRequestPlaceholders}
        autoTokenResponseExample={autoTokenResponseExample}
        autoTokenResponsePath={autoTokenResponsePath}
        autoTokenHeaderName={autoTokenHeaderName}
        autoTokenHeaderFormat={autoTokenHeaderFormat}
        autoTokenSecret={autoTokenSecret}
        refreshTokenPath={refreshTokenPath}
        refreshTokenEndpoint={refreshTokenEndpoint}
        refreshTokenMethod={refreshTokenMethod}
        refreshTokenRequestHeaders={refreshTokenRequestHeaders}
        refreshTokenRequestBody={refreshTokenRequestBody}
        refreshTokenResponsePath={refreshTokenResponsePath}
        onChange={handleAuthConfigChange}
        errors={validationErrors}
      />
      <HStack justify="flex-end">
        <Button
          onClick={handleSaveAuthorization}
          colorPalette="blue"
          variant="solid"
          size="sm"
          loading={savingAuth}
          disabled={savingAuth}
        >
          <MdCheckCircle />
          {t("app.servers.authorization.saveButton")}
        </Button>
      </HStack>
    </VStack>
  );
};

export default AuthorizationConfigComponent;
